import { useState } from 'react';
import { useRequiredSliderContext } from '../slider-context';

const clamp = (value: number, lower: number, upper: number) =>
  Math.min(Math.max(value, lower), upper);

const countDecimals = (value: number) =>
  Number.isInteger(value) ? 0 : (String(value).split('.')[1]?.length ?? 0);

// Keep only what can form a number: digits, a single leading minus, a single dot
const sanitizeNumericInput = (raw: string, allowNegative: boolean): string => {
  let next = raw.replace(/[^\d.-]/g, '');
  if (!allowNegative) next = next.replace(/-/g, '');
  // Minus only allowed at the very start
  next = next.replace(/(?!^)-/g, '');
  // Keep only the first dot
  const [head, ...rest] = next.split('.');
  return rest.length ? `${head}.${rest.join('')}` : next;
};

export interface SliderInputControlledProps {
  type: 'text';
  disabled?: boolean;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur: () => void;
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
}

/**
 * Reads the slider state from context and:
 * - Returns the controlled props for each input
 * - Applies numbers-only entry
 * - Clamps to min/max
 * - Snaps to step
 * - And keeps a range's lower thumb <= upper thumb
 */
export const useSliderInputs = () => {
  const { values, min, max, step, minStepsBetweenThumbs, disabled, onValueChange, onValueCommit } =
    useRequiredSliderContext();

  const isRange = values.length === 2;
  const allowNegative = min < 0;
  const inputMode: 'decimal' | 'numeric' =
    allowNegative || !Number.isInteger(step) ? 'decimal' : 'numeric';

  // editing buffer so intermediate text (e.g. "" or "1" on the way to "15")
  // doesn't update the Slider thumb value
  const [editingValues, setEditingValues] = useState<Record<number, string>>({});

  const getDisplayValue = (index: number) => editingValues[index] ?? String(values[index] ?? '');

  // get number of decimals in step/min.
  // needed to round the snapped result to the precision
  // implied by step/min, to avoid artifacts
  const decimals = Math.max(countDecimals(step), countDecimals(min));

  const round = (value: number) => (decimals ? Number(value.toFixed(decimals)) : value);

  const snapToStep = (value: number) => {
    if (!step) return value;
    const snapped = Math.round((value - min) / step) * step + min;
    return clamp(round(snapped), min, max);
  };

  const handleChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = sanitizeNumericInput(event.target.value, allowNegative);
    setEditingValues((prev) => ({ ...prev, [index]: next }));
  };

  const commit = (index: number) => {
    const raw = editingValues[index];

    // drop this input's draft so getDisplayValue falls back
    // to the committed slider value
    setEditingValues((prev) => {
      const { [index]: _omitted, ...rest } = prev;
      return rest;
    });

    if (raw === undefined) return;

    const current = values[index] ?? min;
    const parsed = Number(raw);
    const isEmptyOrInvalid = raw.trim() === '' || Number.isNaN(parsed);

    const nextValues = [...values];
    nextValues[index] = isEmptyOrInvalid ? current : snapToStep(clamp(parsed, min, max));

    // keep at least minStepsBetweenThumbs between the thumbs for ranges
    // (gap of 0 collapses to lower <= upper)
    if (isRange) {
      const [lower, upper] = nextValues;
      const gap = round(step * minStepsBetweenThumbs);
      if (index === 0) nextValues[0] = Math.min(lower ?? min, round((upper ?? max) - gap));
      else nextValues[1] = Math.max(upper ?? max, round((lower ?? min) + gap));
    }

    if (nextValues[index] !== values[index]) {
      onValueChange(nextValues);
      onValueCommit?.(nextValues);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') event.currentTarget.blur();
  };

  const getControlledProps = (index: number): SliderInputControlledProps => ({
    // type 'text' (not 'number') so we keep full control of the raw value
    type: 'text',
    disabled,
    value: getDisplayValue(index),
    onChange: handleChange(index),
    onBlur: () => commit(index),
    onKeyDown: handleKeyDown,
  });

  return { isRange, inputMode, getControlledProps };
};
